import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { visitorId } from "@/lib/track";
import { useLang } from "@/lib/i18n";
import { useAuth, useConfig, homeFor } from "@/lib/auth";
import { Logo, LangSwitch, Field } from "@/components/xd/ui";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/xd/PasswordInput";

export const helpUrl = (wa, t) => `https://wa.me/${(wa || "972502917236").replace(/\D/g, "")}?text=${encodeURIComponent(t("help.message"))}`;

export const LoginHelp = ({ testid = "login-help-whatsapp" }) => {
  const { t } = useLang();
  const { whatsapp } = useConfig();
  return (
    <a href={helpUrl(whatsapp, t)} target="_blank" rel="noopener noreferrer" data-testid={testid}
      className="flex items-center gap-3 border border-emerald-500/30 bg-emerald-500/[0.06] px-4 py-3 text-sm text-emerald-200 hover:bg-emerald-500/10 transition-colors min-h-[3rem]">
      <MessageCircle size={18} className="shrink-0" /><span>{t("help.login_issue")}</span>
    </a>
  );
};

export const AuthFrame = ({ title, subtitle, children }) => {
  const { sms_test_mode } = useConfig();
  const { t } = useLang();
  return (
    <div className="min-h-screen bg-black text-white grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden">
        <img src="/hero.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-l from-black via-black/30 to-transparent" />
      </div>
      <div className="flex flex-col px-6 py-8 md:px-16">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="auth-home-link"><Logo className="h-12" testid="auth-logo" /></Link>
          <LangSwitch testid="auth-lang" />
        </div>
        <div className="flex-1 flex flex-col justify-center max-w-md w-full mx-auto py-12">
          <h1 className="text-3xl md:text-4xl font-semibold chrome-text">{title}</h1>
          {subtitle && <p className="mt-3 text-zinc-400 text-sm">{subtitle}</p>}
          <div className="xd-line my-8" />
          {sms_test_mode && <p className="mb-6 border border-amber-600/60 bg-amber-950/30 text-amber-300 text-xs p-3" data-testid="sms-test-mode-banner">{t("auth.test_mode")}</p>}
          {children}
        </div>
      </div>
    </div>
  );
};

export const PhoneInput = (props) => <Input type="tel" dir="ltr" inputMode="tel" autoComplete="tel" placeholder="050-000-0000" className="xd-input" {...props} />;
export const CodeInput = (props) => <Input dir="ltr" inputMode="numeric" autoComplete="one-time-code" maxLength={8} placeholder="••••••" className="xd-input tracking-[0.5em] text-center" {...props} />;

export default function Login() {
  const { t, tErr } = useLang();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const as = sp.get("as") || "broker";
  const [form, setForm] = useState({ phone: "", password: "" });
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const done = (u) => { setUser(u); navigate(u.must_change_password ? "/change-password" : homeFor(u), { replace: true }); };
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(tErr(e)); } finally { setBusy(false); }
  };
  const submit = (e) => {
    e.preventDefault();
    run(async () => {
      const { data } = await api.post("/auth/login", { ...form, visitor_id: visitorId() });
      if (data.otp_required) setChallenge(data.challenge_id); else done(data.user);
    });
  };
  const verify = (e) => {
    e.preventDefault();
    run(async () => { const { data } = await api.post("/auth/login/otp", { challenge_id: challenge, code }); done(data.user); });
  };

  return (
    <AuthFrame title={t(`login.title_${as}`)} subtitle={t("login.subtitle")}>
      {challenge ? (
        <form onSubmit={verify} className="space-y-5" data-testid="admin-otp-form">
          <p className="text-sm text-zinc-400">{t("login.otp_sent")}</p>
          <Field label={t("auth.code")}><CodeInput value={code} onChange={(e) => setCode(e.target.value)} data-testid="login-otp-input" /></Field>
          <button disabled={busy} className="btn-xd w-full" data-testid="login-otp-submit">{t("auth.verify")}</button>
        </form>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="login-form">
          <Field label={t("form.phone")}><PhoneInput required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="login-phone-input" /></Field>
          <Field label={t("form.password")}><PasswordInput required autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} testid="login-password-input" /></Field>
          <button disabled={busy} className="btn-xd w-full" data-testid="login-submit-button">{t("auth.login")}</button>
          <div className="flex flex-wrap justify-between gap-3 text-sm text-zinc-500">
            <Link to="/reset" className="hover:text-white" data-testid="forgot-password-link">{t("auth.forgot")}</Link>
            {as !== "admin" && <Link to={`/apply/${as === "dealer" ? "dealer" : "broker"}`} className="hover:text-white" data-testid="apply-link">{t(`apply.link_${as === "dealer" ? "dealer" : "broker"}`)}</Link>}
          </div>
          <LoginHelp />
        </form>
      )}
    </AuthFrame>
  );
}
